/**
 * Font embedding for exports (PDF / PNG / JPEG / print / thumbnails).
 *
 * The page is rasterised by painting the DOM into an SVG <foreignObject> (modern-screenshot). That image is an isolated
 * document: it cannot reach the fonts the page loaded over the network, so every web font has to travel inside it as an
 * @font-face rule with a data: URL. modern-screenshot can only do that for stylesheets whose rules the page may read —
 * fonts loaded on demand from Google Fonts with a <link> (cross-origin, `cssRules` throws) and user fonts registered
 * through the FontFace API were silently dropped, and the export fell back to Arial while the glyph positions had been
 * measured with the real font. This module builds the complete @font-face CSS for the families a render uses.
 */
import { API } from './projectApi';
import { getUserFonts } from './fontLoader';

const textCache = new Map(); // stylesheet url -> Promise<string>
const dataUrlCache = new Map(); // font file url -> Promise<string | null>

const fetchText = (url) => {
  if (!textCache.has(url)) {
    textCache.set(url, fetch(url, { mode: 'cors', credentials: 'omit' }).then((r) => (r.ok ? r.text() : '')).catch(() => ''));
  }
  return textCache.get(url);
};

const toDataUrl = (url) => {
  if (!dataUrlCache.has(url)) {
    const sameOrigin = url.startsWith(window.location.origin) || url.startsWith(API);
    const p = fetch(url, { mode: 'cors', credentials: sameOrigin ? 'include' : 'omit' })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => (blob ? new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(null); fr.readAsDataURL(blob); }) : null))
      .catch(() => null);
    dataUrlCache.set(url, p);
  }
  return dataUrlCache.get(url);
};

const cleanFamily = (f) => String(f || '').split(',')[0].replace(/["']/g, '').trim();
const URL_RE = /url\((['"]?)([^)'"]+)\1\)/g;

/** @font-face blocks of a CSS text: [{ family, cssText, ranges, baseUrl }]. */
const parseFontFaces = (css, baseUrl) => {
  const out = [];
  const re = /@font-face\s*{[^}]*}/g;
  let m;
  while ((m = re.exec(css))) {
    const block = m[0];
    const fam = block.match(/font-family\s*:\s*([^;]+)/i);
    if (!fam) continue;
    const ur = block.match(/unicode-range\s*:\s*([^;]+)/i);
    out.push({ family: cleanFamily(fam[1]), cssText: block, ranges: ur ? parseRanges(ur[1]) : null, baseUrl });
  }
  return out;
};

const parseRanges = (text) => String(text).split(',').map((r) => r.trim()).filter(Boolean).map((r) => {
  const mm = r.match(/^U\+([0-9A-F?]+)(?:-([0-9A-F]+))?$/i);
  if (!mm) return null;
  if (mm[1].includes('?')) { const lo = parseInt(mm[1].replace(/\?/g, '0'), 16); const hi = parseInt(mm[1].replace(/\?/g, 'F'), 16); return [lo, hi]; }
  const lo = parseInt(mm[1], 16);
  return [lo, mm[2] ? parseInt(mm[2], 16) : lo];
}).filter(Boolean);

const rangesHit = (ranges, codes) => {
  if (!ranges || !ranges.length) return true;
  for (const c of codes) for (const [lo, hi] of ranges) if (c >= lo && c <= hi) return true;
  return false;
};

/** Every @font-face the page declares (readable sheets, imported sheets, cross-origin <link> sheets) plus user fonts. */
const collectFontFaces = async () => {
  const faces = [];
  const sheets = Array.from(document.styleSheets);
  await Promise.all(sheets.map(async (sheet) => {
    let rules = null;
    try { rules = sheet.cssRules; } catch { rules = null; }
    if (!rules) {
      if (sheet.href) faces.push(...parseFontFaces(await fetchText(sheet.href), sheet.href));
      return;
    }
    const base = sheet.href || window.location.href;
    await Promise.all(Array.from(rules).map(async (rule) => {
      if (typeof CSSImportRule !== 'undefined' && rule instanceof CSSImportRule) {
        faces.push(...parseFontFaces(await fetchText(rule.href), rule.href));
      } else if (typeof CSSFontFaceRule !== 'undefined' && rule instanceof CSSFontFaceRule) {
        faces.push(...parseFontFaces(rule.cssText, base));
      }
    }));
  }));
  // User fonts (Font ▸ My fonts) live in document.fonts only
  for (const f of getUserFonts()) {
    faces.push({ family: f.family, ranges: null, baseUrl: API, cssText: `@font-face{font-family:"${f.family}";font-weight:${f.weight || 400};font-style:${f.italic ? 'italic' : 'normal'};src:url(${API}/fonts/user/${f.id}/file)}` });
  }
  return faces;
};

const resolveUrl = (url, base) => { try { return new URL(url, base).href; } catch { return url; } };

/**
 * @font-face CSS (fonts inlined as data: URLs) for `families`, limited to the unicode-range subsets that `text` needs.
 * Returns '' when none of the families is a web font (system fonts need nothing).
 */
export const embeddedFontCss = async (families, text = '') => {
  const wanted = new Set(families.map(cleanFamily).filter(Boolean).map((f) => f.toLowerCase()));
  if (!wanted.size) return '';
  const codes = new Set();
  for (const ch of String(text)) codes.add(ch.codePointAt(0));
  const faces = (await collectFontFaces()).filter((f) => wanted.has(f.family.toLowerCase()) && rangesHit(f.ranges, codes));
  const seen = new Set();
  const parts = await Promise.all(faces.map(async (f) => {
    if (seen.has(f.cssText)) return '';
    seen.add(f.cssText);
    const urls = [];
    f.cssText.replace(URL_RE, (raw, q, u) => { urls.push([raw, u]); return raw; });
    let css = f.cssText;
    for (const [raw, u] of urls) {
      if (u.startsWith('data:')) continue;
      const data = await toDataUrl(resolveUrl(u, f.baseUrl));
      if (data) css = css.split(raw).join(`url(${data})`);
    }
    return css;
  }));
  return parts.filter(Boolean).join('\n');
};

/** Distinct first font families used by the elements under `root` (computed styles). */
export const familiesIn = (root) => {
  const out = new Set();
  if (!root) return [];
  const all = [root, ...root.querySelectorAll('*')];
  for (const el of all) {
    if (!(el instanceof Element)) continue;
    const fam = cleanFamily(getComputedStyle(el).fontFamily);
    if (fam) out.add(fam);
  }
  return [...out];
};
